import { isLegacyEngine } from '@/device/legacyEngine'
import {
    DeviceType,
    getSearchParams,
    getUserAgentInfo,
    IllegalArgumentError,
    Os,
    type ReadonlyRecord,
} from '@amazon/vinyl-util'
import type { KeyMap } from '@/device/input/KeyMap'
import type { AmznStreamingOptions } from '@amzn/vinyl-amzn'
import {
    constrainStreamingOptions,
    performanceProfile,
    type PerformanceProfile,
} from '@/device/performanceProfile'
import { IS_TAURI } from '@/util/platform'

export type DeviceTypeName =
    | 'BigScreenDemo'
    | 'Comcast'
    | 'DesktopApp'
    | 'E2eDesktopCbl'
    | 'E2eMobileCbl'
    | 'Hisense'
    | 'LgTv'
    | 'MobileApp'
    | 'Samsung'
    | 'SamsungTv'
    | 'Sky'
    | 'VegaTv'
    | 'Vizio'
    | 'WebPlayer'
    | 'WebPlayerMobile'
    | 'Xbox'

export type AuthType = 'cbl' | 'desktop' | 'map' | 'web'

export const PERFORMANCE_TIERS = ['high', 'medium', 'low'] as const

/**
 * What the device can afford, in rendering, memory and decode work:
 * - 'high' — desktop and mobile browsers; the full visual language and Vinyl defaults.
 * - 'medium' — TVs; drops backdrop blur, trims Vinyl prefetch and track cache, caps audio at
 *   48 kHz with no UHD.
 * - 'low' — Comcast, Sky and legacy-engine (Chrome-52-class) browsers; additionally drops heavy
 *   shadows and the visualizer's heavy scenes, turns reduce-motion on by default, requests 1x
 *   images only, prefetches no routes or pages and keeps only the live route's view.
 *
 * One tier covers memory and CPU: on every device we ship, the two constraints arrive together
 * (the same set-top SoCs have the least RAM and the slowest cores), and no behavior needs one
 * without the other. Split them if a device ever has plenty of one and little of the other.
 */
export type PerformanceTier = (typeof PERFORMANCE_TIERS)[number]

export function isPerformanceTier(value: string | null): value is PerformanceTier {
    return (PERFORMANCE_TIERS as readonly string[]).includes(value ?? '')
}

/**
 * Who shows a keyboard when a text field takes focus:
 * - 'native' — the platform raises its own on-screen keyboard (IME), on focus or on OK.
 * - 'app' — the platform has none for web content, so the app shows its own D-pad keyboard.
 * - 'none' — no on-screen keyboard is needed: the device has a physical keyboard.
 *
 * Per-platform evidence and assumptions are in docs/device-keys.md ("On-screen keyboard").
 */
export type OnScreenKeyboard = 'native' | 'app' | 'none'

/**
 * Where the Help page sends feedback and bug reports (`?feedbackReporter=gh|cloudWatch`
 * overrides it for a page load):
 * - 'gh' — the retail feedback form endpoint, as Green Hornet posts it. No device uses it for
 *   now; it stays available through the override.
 * - 'cloudWatch' — the Vinyl CloudWatch logger, as a `type: 'feedback'` event. Every device uses
 *   this, so all feedback shows in the Vinyl CloudWatch viewer.
 *
 * Event schema and how to find the events: docs/feedback-reporting.md.
 */
export type FeedbackReporterKind = 'gh' | 'cloudWatch'

export const FEEDBACK_REPORTER_KINDS: readonly FeedbackReporterKind[] = ['gh', 'cloudWatch']

/** The product features this device offers; each may still be withheld by its own runtime check. */
export interface DeviceFeatures {
    /**
     * Off: the app withholds the visualizer and gives 'device' as the reason. On, the browser must
     * still be able to run it (`supportsVisualizer`). Bridged playback turns it off at runtime,
     * since the host leaves the web view's audio silent.
     */
    readonly visualizer: boolean
    /** Off: the app is always dark and Settings has no theme picker. */
    readonly lightMode: boolean
}

export interface DeviceConfig {
    /**
     * The type of device, e.g. 'smarttv', 'desktop', 'mobile', 'wearable'
     * This can differ from the user agent detected device type; when a device type id is set, the
     * device type is explicit and not inferred from user agent.
     */
    readonly deviceType: DeviceType

    /**
     * The Device Type ID, or DTID, used by Amazon backend services for conditional allow lists.
     */
    readonly deviceTypeId: string

    /**
     * The authentication implementation this device signs in with:
     * - 'cbl' — code-based linking (Smart TVs)
     * - 'desktop' — the desktop shell's Auth Portal sign-in and Panda device tokens
     * - 'map' — MAP authentication (Vega devices)
     * - 'web' — Login With Amazon (web browser)
     *
     * The cookie-session (AuthPortal) flow is not device-intrinsic — it is chosen
     * by URL param / host / cookie flag in `authRef`, ahead of this field.
     */
    readonly authType: AuthType

    /**
     * Key mappings.
     */
    readonly keyMap: KeyMap

    /**
     * If true, arrow keys will navigate spatially.
     */
    readonly useSpatialNavigation: boolean

    /**
     * Explicitly enables or disables HD/UHD settings.
     */
    readonly streamingOptions: AmznStreamingOptions

    /** True on a WebKit media stack (Safari, WKWebView), where the HD tier and volume normalization don't play. */
    readonly webKit: boolean

    /**
     * The device's default performance tier; `?perf=high|medium|low` overrides it.
     */
    readonly performanceTier: PerformanceTier

    readonly onScreenKeyboard: OnScreenKeyboard

    readonly feedbackReporter: FeedbackReporterKind

    readonly features: DeviceFeatures

    /**
     * True when the hosted build's invite wall applies. A native shell (the Tauri apps, the Vega
     * app's web view) is a trusted distribution channel and boots without an invite.
     */
    readonly requiresInvite: boolean

    /**
     * True inside an installed shell with native bindings, today the Tauri apps. Web and TV
     * browsers are false. A profile declares it, and it holds only where the shell's
     * `__TAURI_INTERNALS__` global is present, so `?device=DesktopApp` in a browser stays false.
     */
    readonly installed: boolean
}

export const defaultKeyBindings = {
    down: 'ArrowDown',
    enter: 'Enter',
    left: 'ArrowLeft',
    right: 'ArrowRight',
    up: 'ArrowUp',
    back: 'GoBack',
    escape: 'Escape',
    playPause: 'MediaPlayPause',
    play: 'MediaPlay',
    pause: 'MediaPause',
    stop: 'MediaStop',
    next: 'MediaTrackNext',
    previous: 'MediaTrackPrevious',
    fastForward: ['MediaFastForward', 'BracketRight'],
    rewind: ['MediaRewind', 'BracketLeft'],
} as const satisfies KeyMap

/** A TV acts only on its remote's keys: Backspace is Back, and a raw Escape is not a key it has. */
export const tvKeyBindings = {
    ...defaultKeyBindings,
    back: [defaultKeyBindings.back, 'Backspace'],
    escape: [],
} as const satisfies KeyMap

const escapeRemoteKeyBindings = {
    ...tvKeyBindings,
    back: [...tvKeyBindings.back, 'Escape'],
    escape: 'Escape',
} as const satisfies KeyMap

export const defaultConfig = {
    keyMap: defaultKeyBindings,
    useSpatialNavigation: true,
    streamingOptions: {
        uhdEnabled: false,
        hdEnabled: false,
    },
    webKit: false,
    performanceTier: 'high',
    onScreenKeyboard: 'none',
    feedbackReporter: 'cloudWatch',
    features: { visualizer: true, lightMode: true },
    requiresInvite: true,
    installed: false,
} as const satisfies Partial<DeviceConfig>

const tizenKeyBindings = {
    ...tvKeyBindings,
    back: [...tvKeyBindings.back, 'XF86Back'],
} as const satisfies KeyMap

const tvConfig = {
    ...defaultConfig,
    keyMap: tvKeyBindings,
    deviceType: DeviceType.SMART_TV,
    onScreenKeyboard: 'app',
    features: { ...defaultConfig.features, lightMode: false },
} as const satisfies Partial<DeviceConfig>

// Vinyl provides this but defining it here lets us lazy-load the player.
const webKit = typeof WebKitMediaKeys !== 'undefined'

// Morpho's desktop device types, the ones the desktop shell registers (Windows, else Mac).
const desktopDeviceTypeId =
    getUserAgentInfo().os?.name === Os.WINDOWS ? 'A1CTGXB4BA274T' : 'A2JTEGS8GUPDOF'

export const deviceConfigs = {
    Comcast: {
        ...tvConfig,
        performanceTier: 'low',
        deviceTypeId: 'A3AUT5UO72X6S8',
        authType: 'cbl',
    },
    Sky: {
        ...tvConfig,
        performanceTier: 'low',
        deviceTypeId: 'A1G6GPHNENL8X1',
        authType: 'cbl',
    },
    Xbox: {
        ...tvConfig,
        onScreenKeyboard: 'native',
        performanceTier: 'medium',
        deviceTypeId: 'A9AIM1JKFYHZW',
        authType: 'cbl',
        keyMap: { ...tvKeyBindings, back: [...tvKeyBindings.back, 'GamepadB'] },
    },
    Vizio: {
        ...tvConfig,
        performanceTier: 'medium',
        deviceTypeId: 'A3QT3Y2HH9BSDB',
        authType: 'cbl',
    },
    Hisense: {
        ...tvConfig,
        performanceTier: 'medium',
        deviceTypeId: 'A27F3L5WMHDOWD',
        authType: 'cbl',
    },
    Samsung: {
        ...tvConfig,
        onScreenKeyboard: 'native',
        performanceTier: 'medium',
        deviceTypeId: 'A15SKKXQB6RPU4',
        authType: 'cbl',
        keyMap: tizenKeyBindings,
    },
    BigScreenDemo: {
        ...tvConfig,
        performanceTier: 'medium',
        deviceTypeId: 'A3DNCSDVBULX7K',
        authType: 'cbl',
        keyMap: escapeRemoteKeyBindings,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
    },
    SamsungTv: {
        ...tvConfig,
        onScreenKeyboard: 'native',
        performanceTier: 'medium',
        deviceTypeId: 'A1CH8LNF5OMH4W',
        authType: 'cbl',
        keyMap: tizenKeyBindings,
    },
    LgTv: {
        ...tvConfig,
        onScreenKeyboard: 'native',
        performanceTier: 'medium',
        deviceTypeId: 'A3LA286UB8D94R',
        authType: 'cbl',
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
    },
    VegaTv: {
        ...tvConfig,
        onScreenKeyboard: 'native',
        performanceTier: 'medium',
        deviceTypeId: 'A1TGF7GBNNZ9EE',
        authType: 'map',
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        requiresInvite: false,
    },
    // E2e-only: CBL-auth desktop/mobile configs. The DTID is the CBL test
    // device the e2e refresh tokens are minted for; authType 'cbl' routes to
    // CblAuth while the desktop/mobile deviceType keeps the real web UX.
    E2eDesktopCbl: {
        ...defaultConfig,
        deviceType: DeviceType.DESKTOP,
        deviceTypeId: 'A3AUT5UO72X6S8',
        authType: 'cbl',
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        webKit,
    },
    E2eMobileCbl: {
        ...defaultConfig,
        onScreenKeyboard: 'native',
        deviceType: DeviceType.MOBILE,
        deviceTypeId: 'A3AUT5UO72X6S8',
        authType: 'cbl',
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        webKit,
    },
    WebPlayer: {
        ...defaultConfig,
        deviceTypeId: 'A16ZV8BU3SN1N3',
        deviceType: DeviceType.DESKTOP,
        authType: 'web',
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        webKit,
    },
    WebPlayerMobile: {
        ...defaultConfig,
        onScreenKeyboard: 'native',
        deviceTypeId: 'A34OIAVFALWTNH',
        deviceType: DeviceType.MOBILE,
        authType: 'web',
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        webKit,
    },
    // The Tauri shells, without the invite wall. The desktop app signs in as a registered desktop
    // device; the mobile app keeps the mobile web player's device type and sign-in.
    DesktopApp: {
        ...defaultConfig,
        deviceTypeId: desktopDeviceTypeId,
        deviceType: DeviceType.DESKTOP,
        authType: 'desktop',
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        webKit,
        requiresInvite: false,
        installed: true,
    },
    MobileApp: {
        ...defaultConfig,
        onScreenKeyboard: 'native',
        deviceTypeId: 'A34OIAVFALWTNH',
        deviceType: DeviceType.MOBILE,
        authType: 'web',
        useSpatialNavigation: false,
        streamingOptions: {
            uhdEnabled: true,
            hdEnabled: true,
        },
        webKit,
        requiresInvite: false,
        installed: true,
    },
} as const satisfies ReadonlyRecord<DeviceTypeName, DeviceConfig>

let deviceConfig: DeviceConfig | undefined

export function getDeviceConfig(): DeviceConfig {
    if (!deviceConfig) {
        const searchParams = getSearchParams()
        // `?mobile=true` forces the mobile device config regardless of UA /
        // viewport / touch capability. Lets puppeteer snap the mobile layout
        // over the real login-safe origin without spoofing the UA string.
        const mobileOverride = searchParams.get('mobile') === 'true'
        const isMobile = mobileOverride || getUserAgentInfo().device?.type === DeviceType.MOBILE
        const deviceTypeName =
            searchParams.get('device') ??
            (IS_TAURI
                ? isMobile
                    ? 'MobileApp'
                    : 'DesktopApp'
                : isMobile
                  ? 'WebPlayerMobile'
                  : 'WebPlayer')
        if (!(deviceTypeName in deviceConfigs)) {
            throw new IllegalArgumentError('Unknown device type: ' + deviceTypeName)
        }
        const base: DeviceConfig = deviceConfigs[deviceTypeName as DeviceTypeName]
        const perf = searchParams.get('perf')
        const performanceTier = isPerformanceTier(perf)
            ? perf
            : isLegacyEngine()
              ? 'low'
              : base.performanceTier
        const streamingOptions = constrainStreamingOptions(base.streamingOptions, performanceTier)
        const installed = base.installed && IS_TAURI
        deviceConfig =
            performanceTier === base.performanceTier &&
            streamingOptions === base.streamingOptions &&
            installed === base.installed
                ? base
                : { ...base, performanceTier, streamingOptions, installed }
    }
    return deviceConfig
}

export function devicePerformanceProfile(): PerformanceProfile {
    return performanceProfile(getDeviceConfig().performanceTier)
}

/** Takes `feature` away for the rest of the session, when something found at boot rules it out. */
export function withdrawDeviceFeature(feature: keyof DeviceFeatures): void {
    const config = getDeviceConfig()
    deviceConfig = { ...config, features: { ...config.features, [feature]: false } }
}

export function isMobile(): boolean {
    return getDeviceConfig().deviceType === DeviceType.MOBILE
}

export function isDesktop(): boolean {
    return getDeviceConfig().deviceType === DeviceType.DESKTOP
}

/** The Tauri desktop app, as opposed to the desktop web player. */
export function isInstalledDesktop(): boolean {
    const { installed, deviceType } = getDeviceConfig()
    return installed && deviceType === DeviceType.DESKTOP
}

/**
 * A convenience reference to the device config isTv
 */
export function isTv(): boolean {
    return getDeviceConfig().deviceType === DeviceType.SMART_TV
}
